import type { BadgeColor } from './badge'

/**
 * The assignable stage-badge palette — the categorical (non-semantic) colors, in
 * the app's canonical spectrum order. Mirrors `STAGE_PALETTE_KEYS` in the client
 * (client/src/components/SalesStage/stageColors.ts); keep the two in sync. Lives
 * here (core) rather than in a block so any layer — the stage-config modals, the
 * pipeline widgets, story fixtures — can share one source.
 */
export const STAGE_COLORS = [
  'blue',
  'indigo',
  'sky',
  'cyan',
  'teal',
  'emerald',
  'lime',
  'yellow',
  'amber',
  'orange',
  'rose',
  'pink',
  'fuchsia',
  'purple',
  'violet',
] as const satisfies readonly BadgeColor[]

/**
 * Shared color-token maps for the badge palette, single-sourced so `Badge`,
 * `StatusStat`, and `Stat` all resolve a given color to the same shades.
 * Tailwind needs literal class strings, so each role is a full record rather
 * than a constructed `bg-fbui-${color}-${shade}` (which JIT can't see).
 *
 *  - **softSurface** — the soft fill (-50, gray uses -100): pill/band background,
 *    icon-chip background.
 *  - **softText** — the -700 foreground paired with softSurface.
 *  - **accentText** — the brighter accent (-500/-600) for standalone icons,
 *    matching the Badge status dot.
 */

/** Soft fill per color (gray -100, others -50). */
export const softSurface: Record<BadgeColor, string> = {
  gray: 'bg-fbui-gray-100',
  primary: 'bg-fbui-basil-50',
  error: 'bg-fbui-red-50',
  warning: 'bg-fbui-amber-50',
  success: 'bg-fbui-green-50',
  cyan: 'bg-fbui-cyan-50',
  sky: 'bg-fbui-sky-50',
  blue: 'bg-fbui-blue-50',
  indigo: 'bg-fbui-indigo-50',
  violet: 'bg-fbui-violet-50',
  purple: 'bg-fbui-purple-50',
  fuchsia: 'bg-fbui-fuchsia-50',
  pink: 'bg-fbui-pink-50',
  rose: 'bg-fbui-rose-50',
  orange: 'bg-fbui-orange-50',
  amber: 'bg-fbui-amber-50',
  yellow: 'bg-fbui-yellow-50',
  lime: 'bg-fbui-lime-50',
  emerald: 'bg-fbui-emerald-50',
  teal: 'bg-fbui-teal-50',
}

/** -700 foreground per color, paired with {@link softSurface}. */
export const softText: Record<BadgeColor, string> = {
  gray: 'text-fbui-gray-700',
  primary: 'text-fbui-basil-700',
  error: 'text-fbui-red-700',
  warning: 'text-fbui-amber-700',
  success: 'text-fbui-green-700',
  cyan: 'text-fbui-cyan-700',
  sky: 'text-fbui-sky-700',
  blue: 'text-fbui-blue-700',
  indigo: 'text-fbui-indigo-700',
  violet: 'text-fbui-violet-700',
  purple: 'text-fbui-purple-700',
  fuchsia: 'text-fbui-fuchsia-700',
  pink: 'text-fbui-pink-700',
  rose: 'text-fbui-rose-700',
  orange: 'text-fbui-orange-700',
  amber: 'text-fbui-amber-700',
  yellow: 'text-fbui-yellow-700',
  lime: 'text-fbui-lime-700',
  emerald: 'text-fbui-emerald-700',
  teal: 'text-fbui-teal-700',
}

/**
 * On-color ring per color — the soft fill a couple steps darker (-200; gray
 * -300), so a soft chip stays delineated on a tinted surface without a neutral
 * grey outline.
 */
export const softRing: Record<BadgeColor, string> = {
  gray: 'ring-fbui-gray-300',
  primary: 'ring-fbui-basil-200',
  error: 'ring-fbui-red-200',
  warning: 'ring-fbui-amber-200',
  success: 'ring-fbui-green-200',
  cyan: 'ring-fbui-cyan-200',
  sky: 'ring-fbui-sky-200',
  blue: 'ring-fbui-blue-200',
  indigo: 'ring-fbui-indigo-200',
  violet: 'ring-fbui-violet-200',
  purple: 'ring-fbui-purple-200',
  fuchsia: 'ring-fbui-fuchsia-200',
  pink: 'ring-fbui-pink-200',
  rose: 'ring-fbui-rose-200',
  orange: 'ring-fbui-orange-200',
  amber: 'ring-fbui-amber-200',
  yellow: 'ring-fbui-yellow-200',
  lime: 'ring-fbui-lime-200',
  emerald: 'ring-fbui-emerald-200',
  teal: 'ring-fbui-teal-200',
}

/**
 * A one-step-darker ring than {@link softRing} (-300 vs -200) — for a colored
 * soft-fill chip that needs a *visible* outline against a tinted surface (e.g.
 * the StageTracker's current-stage pill on the gray deal header, where a -200
 * ring washes out). Matches the gray-300 border weight of the neutral clusters
 * beside it, so colored and neutral controls share one outline weight.
 */
export const midRing: Record<BadgeColor, string> = {
  gray: 'ring-fbui-gray-400',
  primary: 'ring-fbui-basil-300',
  error: 'ring-fbui-red-300',
  warning: 'ring-fbui-amber-300',
  success: 'ring-fbui-green-300',
  cyan: 'ring-fbui-cyan-300',
  sky: 'ring-fbui-sky-300',
  blue: 'ring-fbui-blue-300',
  indigo: 'ring-fbui-indigo-300',
  violet: 'ring-fbui-violet-300',
  purple: 'ring-fbui-purple-300',
  fuchsia: 'ring-fbui-fuchsia-300',
  pink: 'ring-fbui-pink-300',
  rose: 'ring-fbui-rose-300',
  orange: 'ring-fbui-orange-300',
  amber: 'ring-fbui-amber-300',
  yellow: 'ring-fbui-yellow-300',
  lime: 'ring-fbui-lime-300',
  emerald: 'ring-fbui-emerald-300',
  teal: 'ring-fbui-teal-300',
}

/**
 * `from-` gradient stop per color — the soft fill (-50, gray -100) as the start
 * of an ombre wash (`bg-gradient-to-* from-… to-transparent`) over a white
 * surface, e.g. the pinned-record tiles. Keep it a plain `from-` token (no
 * `/opacity` — the `fbui-*` colors don't support the alpha modifier, which
 * silently drops the gradient); dial subtlety with the gradient's *reach* (a
 * `to-<pct>` stop) instead. Tailwind needs literal class strings, so this can't
 * be derived from `softSurface`.
 */
export const softGradient: Record<BadgeColor, string> = {
  gray: 'from-fbui-gray-100',
  primary: 'from-fbui-basil-50',
  error: 'from-fbui-red-50',
  warning: 'from-fbui-amber-50',
  success: 'from-fbui-green-50',
  cyan: 'from-fbui-cyan-50',
  sky: 'from-fbui-sky-50',
  blue: 'from-fbui-blue-50',
  indigo: 'from-fbui-indigo-50',
  violet: 'from-fbui-violet-50',
  purple: 'from-fbui-purple-50',
  fuchsia: 'from-fbui-fuchsia-50',
  pink: 'from-fbui-pink-50',
  rose: 'from-fbui-rose-50',
  orange: 'from-fbui-orange-50',
  amber: 'from-fbui-amber-50',
  yellow: 'from-fbui-yellow-50',
  lime: 'from-fbui-lime-50',
  emerald: 'from-fbui-emerald-50',
  teal: 'from-fbui-teal-50',
}

/** Brighter accent foreground per color, for standalone icons. */
export const accentText: Record<BadgeColor, string> = {
  gray: 'text-fbui-gray-500',
  primary: 'text-fbui-basil-500',
  error: 'text-fbui-red-600',
  warning: 'text-fbui-amber-600',
  success: 'text-fbui-green-600',
  cyan: 'text-fbui-cyan-500',
  sky: 'text-fbui-sky-500',
  blue: 'text-fbui-blue-600',
  indigo: 'text-fbui-indigo-500',
  violet: 'text-fbui-violet-500',
  purple: 'text-fbui-purple-500',
  fuchsia: 'text-fbui-fuchsia-500',
  pink: 'text-fbui-pink-500',
  rose: 'text-fbui-rose-500',
  orange: 'text-fbui-orange-500',
  amber: 'text-fbui-amber-600',
  yellow: 'text-fbui-yellow-500',
  lime: 'text-fbui-lime-500',
  emerald: 'text-fbui-emerald-600',
  teal: 'text-fbui-teal-500',
}

/**
 * `bg-` twin of {@link accentText}, used for the Badge status dot. Tailwind needs
 * both as literal class strings, so they can't be derived from each other —
 * **keep the shades in sync** (every entry is the same shade as accentText).
 */
export const accentBg: Record<BadgeColor, string> = {
  gray: 'bg-fbui-gray-500',
  primary: 'bg-fbui-basil-500',
  error: 'bg-fbui-red-600',
  warning: 'bg-fbui-amber-600',
  success: 'bg-fbui-green-600',
  cyan: 'bg-fbui-cyan-500',
  sky: 'bg-fbui-sky-500',
  blue: 'bg-fbui-blue-600',
  indigo: 'bg-fbui-indigo-500',
  violet: 'bg-fbui-violet-500',
  purple: 'bg-fbui-purple-500',
  fuchsia: 'bg-fbui-fuchsia-500',
  pink: 'bg-fbui-pink-500',
  rose: 'bg-fbui-rose-500',
  orange: 'bg-fbui-orange-500',
  amber: 'bg-fbui-amber-600',
  yellow: 'bg-fbui-yellow-500',
  lime: 'bg-fbui-lime-500',
  emerald: 'bg-fbui-emerald-600',
  teal: 'bg-fbui-teal-500',
}
